import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React, { useMemo } from "react";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
];

const buildSafeSearch = (searchParams) => {
  const current = new URLSearchParams(searchParams);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  // ✅ Prev/Next list building ke liye (ConnectDetailsPage)
  next.set("section", "web");

  return `?${next.toString()}`;
};

const WebStory = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const sectionKey = searchParams.get("section"); // ✅ persistent

  // ✅ navbar se aane wala category_name (code)
  const categoryName = searchParams.get("category_name") || "";

  const {
    item: webStory,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: newsPending,
  } = useItemCategoryInfinite({
    key: "connect_web",
    endpoint: "web_stories",
    listKey: "web_stories",
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
    featured: sectionKey === "features" ? 1 : undefined,
  });

  const hasNews = (webStory?.length || 0) > 0;

  /** ✅ Menu data */
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "webstory");
  }, [menuData]);

  const getWeb = useMemo(() => addPosition(webStory), [webStory]);

  /** ✅ Navbar category select */
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  /** ✅ CARD CLICK => navigate to details route (no local state single page) */
  const handleOpenDetails = (item, idx) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    navigate(
      {
        pathname: `${saffronRout}/details/webstory/${slug}`,
        search: buildSafeSearch(searchParams),
      },
      {
        state: {
          sectionKey: "web", // ✅ list fetch key in details page
          position: idx ?? item?.position ?? 0,
        },
      },
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />

      <section className="py-3  px-1 md:px-5 lg:px-10 ">
        {/* List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getWeb?.map((item, idx) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleOpenDetails(item, idx)}
              imgH=" h-[300px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {/* Status */}
        {newsPending && <p className="text-gray-500">Loading articles…</p>}

        {!newsPending && !hasNews && (
          <p className="text-gray-400 italic">
            No articles found in{" "}
            <span className="font-medium">'Web Stories'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default WebStory;
